import { IRAS_CONFIG } from "../../../config/irasConfig";

export const RETURN_COMPANY_STORAGE_KEY = "iras_oauth_return_company";
export const RETURN_LOCATION_STORAGE_KEY = "iras_oauth_return_location";
export const OAUTH_IN_FLIGHT_STORAGE_KEY = "iras_oauth_in_flight";

const STATE_STORAGE_KEY = IRAS_CONFIG.SECURITY_CONFIG.STATE_STORAGE_KEY;
const IN_FLIGHT_TTL_MS = 30 * 60 * 1000;

const writeWebStorage = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch {
    /* Safari private mode / quota */
  }
};

const readWebStorage = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const removeWebStorage = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch {
    /* ignore */
  }
};

/** Write both stores so Safari ITP / new browsing context after Apex still has the value. */
export const writePersistentValue = (key, value) => {
  writeWebStorage(sessionStorage, key, value);
  writeWebStorage(localStorage, key, value);
};

export const readPersistentValue = (key) =>
  readWebStorage(sessionStorage, key) ?? readWebStorage(localStorage, key);

export const removePersistentValue = (key) => {
  removeWebStorage(sessionStorage, key);
  removeWebStorage(localStorage, key);
};

const writePersistentJson = (key, value) => {
  writePersistentValue(key, JSON.stringify(value));
};

const readPersistentJson = (key) => {
  const raw = readPersistentValue(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const markIrasOAuthInFlight = () => {
  writePersistentJson(OAUTH_IN_FLIGHT_STORAGE_KEY, { ts: Date.now() });
};

export const isIrasOAuthInFlight = () => {
  const payload = readPersistentJson(OAUTH_IN_FLIGHT_STORAGE_KEY);
  const ts = Number(payload?.ts);
  if (!Number.isFinite(ts)) return false;
  if (Date.now() - ts > IN_FLIGHT_TTL_MS) {
    clearIrasOAuthInFlight();
    return false;
  }
  return true;
};

export const clearIrasOAuthInFlight = () => {
  removePersistentValue(OAUTH_IN_FLIGHT_STORAGE_KEY);
};

export const persistIrasOAuthState = (state) => {
  if (state == null) return;
  writePersistentValue(STATE_STORAGE_KEY, String(state));
};

export const persistIrasOAuthReturnContext = (company, location) => {
  if (company && Object.keys(company).length > 0) {
    writePersistentJson(RETURN_COMPANY_STORAGE_KEY, company);
  }
  writePersistentJson(RETURN_LOCATION_STORAGE_KEY, {
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
    activeTab: 1,
  });
  markIrasOAuthInFlight();
};

export const readIrasOAuthReturnLocation = () =>
  readPersistentJson(RETURN_LOCATION_STORAGE_KEY);

export const readIrasOAuthReturnCompany = () =>
  readPersistentJson(RETURN_COMPANY_STORAGE_KEY);

export const clearIrasOAuthReturnContext = () => {
  removePersistentValue(RETURN_COMPANY_STORAGE_KEY);
  removePersistentValue(RETURN_LOCATION_STORAGE_KEY);
  removePersistentValue(STATE_STORAGE_KEY);
  clearIrasOAuthInFlight();
};

export const isSafeInternalPath = (pathname) =>
  typeof pathname === "string" &&
  pathname.startsWith("/") &&
  !pathname.startsWith("//") &&
  pathname !== "/iras/callback";

export const buildIrasOAuthReturnTarget = (returnLocation, callbackSearch) => {
  const pathname = isSafeInternalPath(returnLocation?.pathname)
    ? returnLocation.pathname
    : "/company";
  const params = new URLSearchParams(returnLocation?.search || "");
  const callbackParams = new URLSearchParams(callbackSearch || "");
  callbackParams.forEach((value, key) => params.set(key, value));
  const search = params.toString();
  const hash =
    typeof returnLocation?.hash === "string" ? returnLocation.hash : "";
  return {
    pathname,
    search: search ? `?${search}` : "",
    hash,
  };
};

export const redirectToIrasAuthorization = (response) => {
  const data = response?.data ?? response;
  if (response?.status !== "success" || !data?.authorization_url) {
    throw new Error(
      response?.message || "Failed to initiate IRAS authentication."
    );
  }

  persistIrasOAuthState(data.state);
  window.location.assign(data.authorization_url);
};
